import React, { useRef } from "react";
import { Box } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { TIMES, ymd } from "./EventPicker";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, FONT_DISPLAY, RADIUS, VISUALLY_HIDDEN } from "../../utils/designTokens";

export const GUEST_STEP = 5;
export const GUEST_MIN = 5;
export const GUEST_MAX = 500;
export const GUEST_DEFAULT = 20;

const dayAfter = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
export const tomorrowKey = () => ymd(dayAfter(new Date(), 1));
// The coming Saturday (tomorrow's, on a Friday).
export const weekendKey = () => {
  const today = new Date();
  return ymd(dayAfter(today, ((6 - today.getDay() + 7) % 7) || 7));
};
const fmtDay = (key) => new Date(`${key}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

const fieldValueSx = {
  ...BUTTON_RESET,
  appearance: "none",
  width: "100%",
  minHeight: 28,
  pr: 3,
  fontFamily: FONT_BODY,
  fontSize: 15,
  fontWeight: 700,
  color: C.ink,
  cursor: "pointer",
  textOverflow: "ellipsis",
  "&:focus-visible": { outline: "none" },
};

function Field({ label, htmlFor, children, first, wide }) {
  return (
    <Box
      sx={{
        position: "relative",
        minWidth: 0,
        // On a phone the stepper takes a row of its own.
        gridColumn: wide ? { xs: "1 / -1", md: "auto" } : undefined,
        px: 2,
        py: 1,
        borderRadius: "12px",
        "&:focus-within": { boxShadow: `inset 0 0 0 2px ${C.ink}` },
        // Dividers between the fields on a wide bar.
        "&::before": first
          ? undefined
          : { content: '""', display: { xs: "none", md: "block" }, position: "absolute", left: 0, top: 10, bottom: 10, width: "1px", bgcolor: C.border },
      }}
    >
      <Box component={htmlFor ? "label" : "span"} htmlFor={htmlFor} sx={{ display: "block", fontFamily: FONT_BODY, fontSize: 12, fontWeight: 600, color: C.muted, mb: 0.25 }}>
        {label}
      </Box>
      {children}
    </Box>
  );
}

function SelectBox({ id, value, onChange, children }) {
  return (
    <Box sx={{ position: "relative" }}>
      <Box component="select" id={id} value={value} onChange={(e) => onChange(e.target.value)} sx={fieldValueSx}>
        {children}
      </Box>
      <ExpandMoreRoundedIcon aria-hidden="true" sx={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", fontSize: 20, color: C.muted, pointerEvents: "none" }} />
    </Box>
  );
}

const roundBtn = {
  ...BUTTON_RESET,
  ...FOCUS_RING,
  width: 32,
  height: 32,
  flexShrink: 0,
  borderRadius: "50%",
  border: `1px solid ${C.borderStrong}`,
  color: C.ink,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  // The hit area reaches 44px.
  position: "relative",
  "&::before": { content: '""', position: "absolute", inset: -6 },
  "&:hover:not(:disabled)": { bgcolor: C.surface },
  "&:disabled": { opacity: 0.35, cursor: "default" },
  "& svg": { fontSize: 18 },
};

/**
 * The top of the Catering page: a dark banner with the event planner under
 * its title. plan = { date: "YYYY-MM-DD", time: "HH:MM", guests }; every
 * change applies at once (the caterers below follow it), and Find caterers
 * takes the customer down to them.
 */
function PlannerBanner({ plan, onChange, pickup, onPickup, onFind }) {
  const dateInputRef = useRef(null);
  const tomorrow = tomorrowKey();
  const weekend = weekendKey();
  const dateChoice = plan.date === tomorrow ? "tomorrow" : plan.date === weekend ? "weekend" : "custom";

  const onDateChoice = (v) => {
    if (v === "tomorrow") onChange({ date: tomorrow });
    else if (v === "weekend") onChange({ date: weekend });
    else if (v === "pick") {
      const input = dateInputRef.current;
      if (!input) return;
      try {
        if (typeof input.showPicker === "function") input.showPicker();
        else input.focus();
      } catch (e) {
        input.focus();
      }
    }
  };

  return (
    <Box
      component="section"
      aria-labelledby="planner-title"
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: "24px",
        bgcolor: C.ink,
        color: "#FFFFFF",
        p: { xs: 3, md: "36px" },
      }}
    >
      <Box aria-hidden="true" sx={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", right: -70, top: -110, bgcolor: "rgba(245,183,49,0.14)" }} />
      <Box aria-hidden="true" sx={{ position: "absolute", width: 220, height: 220, borderRadius: "50%", right: 150, top: 60, bgcolor: "rgba(196,43,31,0.22)", display: { xs: "none", sm: "block" } }} />

      <Box sx={{ position: "relative" }}>
        <Box component="span" sx={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.goldAccent }}>
          Catering
        </Box>
        <Box component="h1" id="planner-title" sx={{ m: 0, mt: 0.75, fontFamily: FONT_DISPLAY, fontSize: { xs: 32, sm: 40 }, fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.02em", color: "#FFFFFF" }}>
          Feed the whole crowd
        </Box>
        <Box component="p" sx={{ m: 0, mt: 1, maxWidth: 560, fontFamily: FONT_BODY, fontSize: 16, lineHeight: 1.5, color: "#E8E5DF" }}>
          Trays and party platters from local restaurants, for offices, birthdays and every get-together.
        </Box>

        <Box
          role="group"
          aria-label="Your event"
          sx={{
            mt: 3,
            p: "10px",
            borderRadius: "18px",
            bgcolor: C.bg,
            color: C.ink,
            display: "grid",
            alignItems: "center",
            gap: { xs: 0.5, md: 0 },
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              md: "minmax(150px, 1.2fr) minmax(120px, 1fr) minmax(170px, 1.2fr) auto auto",
            },
          }}
        >
          <Field label="Event date" htmlFor="planner-date" first>
            <SelectBox id="planner-date" value={dateChoice} onChange={onDateChoice}>
              <option value="tomorrow">Tomorrow</option>
              <option value="weekend">This weekend</option>
              {dateChoice === "custom" && <option value="custom">{fmtDay(plan.date)}</option>}
              <option value="pick">Pick a date…</option>
            </SelectBox>
            {/* Opened by "Pick a date…". */}
            <Box
              component="input"
              type="date"
              ref={dateInputRef}
              tabIndex={-1}
              aria-hidden="true"
              min={ymd(new Date())}
              value={plan.date}
              onChange={(e) => e.target.value && onChange({ date: e.target.value })}
              sx={{ ...VISUALLY_HIDDEN, left: 16, bottom: 0 }}
            />
          </Field>

          <Field label="Time" htmlFor="planner-time">
            <SelectBox id="planner-time" value={plan.time} onChange={(time) => onChange({ time })}>
              {TIMES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </SelectBox>
          </Field>

          <Field label="Guests" wide>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 28 }}>
              <Box
                component="button"
                type="button"
                aria-label="Fewer guests"
                disabled={plan.guests <= GUEST_MIN}
                onClick={() => onChange({ guests: Math.max(GUEST_MIN, plan.guests - GUEST_STEP) })}
                sx={roundBtn}
              >
                <RemoveRoundedIcon aria-hidden="true" />
              </Box>
              <Box component="span" aria-live="polite" sx={{ flex: 1, minWidth: 0, textAlign: "center", fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, whiteSpace: "nowrap" }}>
                {plan.guests} people
              </Box>
              <Box
                component="button"
                type="button"
                aria-label="More guests"
                disabled={plan.guests >= GUEST_MAX}
                onClick={() => onChange({ guests: Math.min(GUEST_MAX, plan.guests + GUEST_STEP) })}
                sx={roundBtn}
              >
                <AddRoundedIcon aria-hidden="true" />
              </Box>
            </Box>
          </Field>

          <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" }, px: { xs: 1, md: 1.5 }, position: "relative", "&::before": { content: '""', display: { xs: "none", md: "block" }, position: "absolute", left: 0, top: 10, bottom: 10, width: "1px", bgcolor: C.border } }}>
            <Box role="group" aria-label="Delivery or pickup" sx={{ display: "flex", p: "4px", borderRadius: RADIUS.pill, bgcolor: C.surface2 }}>
              {[
                { label: "Delivery", on: !pickup, value: false },
                { label: "Pickup", on: pickup, value: true },
              ].map((o) => (
                <Box
                  key={o.label}
                  component="button"
                  type="button"
                  aria-pressed={o.on}
                  onClick={() => onPickup(o.value)}
                  sx={{
                    ...BUTTON_RESET,
                    ...FOCUS_RING,
                    flex: 1,
                    height: 40,
                    px: 2,
                    borderRadius: RADIUS.pill,
                    fontFamily: FONT_BODY,
                    fontSize: 14,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    bgcolor: o.on ? C.bg : "transparent",
                    color: o.on ? C.ink : C.muted,
                    boxShadow: o.on ? "0 1px 4px rgba(26,25,24,0.14)" : "none",
                  }}
                >
                  {o.label}
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={onFind}
            sx={{
              ...BUTTON_RESET,
              ...FOCUS_RING,
              gridColumn: { xs: "1 / -1", md: "auto" },
              height: 56,
              px: 3,
              borderRadius: "14px",
              bgcolor: C.red,
              color: "#FFFFFF",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              fontFamily: FONT_BODY,
              fontSize: 16,
              fontWeight: 700,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: C.redHover },
            }}
          >
            <SearchRoundedIcon aria-hidden="true" />
            Find caterers
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default React.memo(PlannerBanner);
